/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useReducer } from "react";
import { When } from "react-if";
import { ReportType, OsType, CreateReportInput } from "~/generated/graphql";
import { PopoverControls } from "~/components";
import { useQueryState } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { inflect, isEmpty } from "~/tools";
import { useCreateReport } from "~/views/resources/hooks/useCreateReport/useCreateReport";
import { ResourceQuery } from "~/views/resources/types";
import { prepareFilter } from "../utils";
import { AddToResourceGroup } from "./AddToGroup/AddToResourceGroup";
import { DeleteResource } from "./DeleteResource/DeleteResource";

type State = {
  isDeleteModalOpen: boolean;
  isAddToGroupModalOpen: boolean;
};

type Props = {
  resourcesDetails: {
    id: string;
    name: string;
    os?: OsType | null;
    scanned: boolean;
  }[];
  onSuccess?: () => void;
  size?: "large" | "small";
  selectedResourceIds?: string[];
  allResourcesSelected?: boolean;
  hasResources?: boolean;
};

export function Actions({
  resourcesDetails,
  size = "small",
  selectedResourceIds = [],
  allResourcesSelected = false,
  hasResources = false,
}: Props) {
  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => {
      return {
        ...data,
        ...partialData,
      };
    },
    {
      isDeleteModalOpen: false,
      isAddToGroupModalOpen: false,
    }
  );

  const { queryState } = useQueryState<ResourceQuery>();

  const [createReport] = useCreateReport();
  const resourcesIds = resourcesDetails.map((item) => item.id);
  const hasSelectedResourceIds = selectedResourceIds.length > 0;
  const noResourcesIds = isEmpty(resourcesIds ?? []) && !hasSelectedResourceIds;
  const canGenerateReport =
    allResourcesSelected || !noResourcesIds || hasResources;

  const getResourceIdsForReport = (): string[] | undefined => {
    if (allResourcesSelected) {
      return [];
    }
    if (selectedResourceIds.length > 0) {
      return selectedResourceIds;
    }
    if (resourcesDetails.length === 1) {
      return [resourcesDetails[0].id]; // Single resource from row actions menu
    }
    return [];
  };

  const handleGenerateReport = async () => {
    const resourceIds = getResourceIdsForReport();
    const mutationInput: CreateReportInput = {
      reportType: ReportType.ResourceListCsvExport,
      resourceListInput: {
        resourceIds: resourceIds ?? [],
        filter: allResourcesSelected ? prepareFilter(queryState) : undefined,
        phrase: queryState.searchPhrase ? queryState.searchPhrase : undefined,
      },
    };
    // to prevent blocked pop-up by browsers
    const newTab = window.open("", "_blank");
    try {
      const reportId = await createReport(mutationInput);
      if (newTab?.location && reportId) {
        // eslint-disable-next-line fp/no-mutation
        newTab.location.href = `${ROUTES.resources.reports}?newReportId=${reportId}`;
      }
    } catch (error) {
      newTab?.close();
    }
  };

  const menuItems = [
    {
      label: `Add to Resource Group`,
      onClick: () => updateState({ isAddToGroupModalOpen: true }),
    },
    {
      label: "Get Resource Details Report (CSV)",
      onClick: handleGenerateReport,
      disabled: !canGenerateReport,
    },
    {
      label: `Delete ${inflect("Resource")(resourcesIds?.length)}`,
      onClick: () => updateState({ isDeleteModalOpen: true }),
    },
  ].filter(Boolean);

  return (
    <>
      <PopoverControls
        disabled={noResourcesIds}
        size={size}
        items={menuItems as NonNullable<typeof menuItems[number]>[]}
      />
      <When condition={state.isDeleteModalOpen}>
        <DeleteResource
          {...{
            resourcesDetails: resourcesDetails,
            isDeleteModalOpen: state.isDeleteModalOpen,
          }}
          close={() => updateState({ isDeleteModalOpen: false })}
        />
      </When>
      <When condition={state.isAddToGroupModalOpen}>
        <AddToResourceGroup
          {...{
            resourcesIds: resourcesIds,
            isOpen: state.isAddToGroupModalOpen,
          }}
          close={() => updateState({ isAddToGroupModalOpen: false })}
        />
      </When>
    </>
  );
}
